import React from "react";
import "./ModuleSelectionHub.css";
import axios from "axios";
import { withRouter } from "react-router-dom";
import CacheStorage from "../../utils/CacheStorage";
import { responseMessage } from "../../utils/alert";
import StorageIcon from "@material-ui/icons/Storage";
import EcoIcon from "@material-ui/icons/Eco";
import TrendingUpIcon from "@material-ui/icons/TrendingUp";
import AssessmentIcon from "@material-ui/icons/Assessment";
import ExploreIcon from "@material-ui/icons/Explore";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import LockOutlinedIcon from "@material-ui/icons/LockOutlined";
import ExitToAppIcon from "@material-ui/icons/ExitToApp";
import DashboardIcon from "@material-ui/icons/Dashboard";
import PublicIcon from "@material-ui/icons/Public";
import amploflyLogo from "../../common/images/amplofly-logo.png";

const config = require("../../config");

const getIconForProduct = (prod, fallback = null) => {
    if (fallback && fallback.icon && React.isValidElement(fallback.icon)) return fallback.icon;
    const iconName = (prod.icon || prod.id || "").toLowerCase();
    if (iconName.includes("carbon")) return <PublicIcon style={{ fontSize: 28 }} />;
    if (iconName.includes("data") || iconName.includes("storage") || prod.accent === "purple") return <StorageIcon style={{ fontSize: 28 }} />;
    if (iconName.includes("sustain") || iconName.includes("eco") || prod.accent === "green") return <EcoIcon style={{ fontSize: 28 }} />;
    if (iconName.includes("pric") || iconName.includes("trend") || prod.accent === "blue") return <TrendingUpIcon style={{ fontSize: 28 }} />;
    if (iconName.includes("rev") || iconName.includes("assess") || prod.accent === "orange") return <AssessmentIcon style={{ fontSize: 28 }} />;
    if (iconName.includes("strat") || iconName.includes("expl") || prod.accent === "teal") return <ExploreIcon style={{ fontSize: 28 }} />;
    return <DashboardIcon style={{ fontSize: 28 }} />;
};

const DEFAULT_PRODUCTS = [
    {
        id: "data_management",
        title: "Unified Data Model",
        subtitle: "Unified data ingestion, templates and framework mapping (BRSR / GRI) feeding every workspace.",
        accent: "purple",
        icon: <StorageIcon style={{ fontSize: 28 }} />,
        features: [
            "Unified data ingestion across ESG & financial data",
            "Automated template and framework mapping (BRSR, GRI, TCFD)",
            "Data quality validation and anomaly detection"
        ],
        status: "licensed",
        module_mapping: ["Unified Data Model", "Data Management", "Reports & Analytics", "Reporting"]
    },
    {
        id: "sustainable_excellence",
        title: "Sustainable Excellence",
        subtitle: "AmpMarking, capability modeling, KPI performance, carbon & reporting across your ESG estate.",
        accent: "green",
        icon: <EcoIcon style={{ fontSize: 28 }} />,
        features: [
            "AmpMarking maturity evaluation and capability modeling",
            "Real-time KPI tracking and performance measuring",
            "Automated carbon accounting and ESG reporting (BRSR)"
        ],
        status: "licensed",
        module_mapping: ["AmpMarking", "Capability Modeling", "Performance Measuring", 'Reports & Analytics', "Reporting", "All"]
    },
    {
        id: "pricing_analysis",
        title: "Pricing Analysis",
        subtitle: "Price-Equivalence Line analytics — reuses benchmarking & growth modeling, re-pointed to the pricing ontology.",
        accent: "blue",
        icon: <TrendingUpIcon style={{ fontSize: 28 }} />,
        features: [
            "Price-Equivalence Line analytics and elasticity modeling",
            "Automated benchmarking against industry peers",
            "Margin optimization and discount governance"
        ],
        status: "licensed",
        module_mapping: ["Pricing Analysis", "AmpMarking", "All"]
    },
    {
        id: "revenue_growth",
        title: "Revenue Growth",
        subtitle: "Model revenue-growth levers, CTS-LTV economics and expansion scenarios.",
        accent: "orange",
        icon: <AssessmentIcon style={{ fontSize: 28 }} />,
        features: [
            "Growth lever modeling: Quantify pricing, volume and mix drivers",
            "CTS-LTV economics: Cost-to-serve vs lifetime value heatmaps",
            "Expansion scenarios: Simulate market and segment expansion"
        ],
        status: "unlicensed",
        module_mapping: ["Revenue Growth", "All"]
    },
    {
        id: "strategy_simulation",
        title: "Strategy Simulation",
        subtitle: "War-game strategic options and stress-test outcomes across markets and competitors.",
        accent: "orange",
        icon: <ExploreIcon style={{ fontSize: 28 }} />,
        features: [
            "Scenario engine: Compare strategic options side by side",
            "Competitive war-gaming: Model competitor responses",
            "Outcome stress-testing: Risk-weight strategic bets"
        ],
        status: "unlicensed",
        module_mapping: ["Strategy Simulation", "All"]
    },
    {
        id: "carbon_accounting",
        title: "Carbon Accounting",
        subtitle: "Automated carbon accounting that measures emissions, identifies reduction pathways, and drives decarbonization decisions.",
        accent: "green",
        icon: <PublicIcon style={{ fontSize: 28 }} />,
        features: [
            "AmpMarking maturity evaluation and capability modeling",
            "Real-time KPI tracking and performance measuring",
            "Automated carbon accounting and ESG reporting (BRSR)"
        ],
        status: "unlicensed",
        module_mapping: ["AmpMarking", "Capability Modeling", "Performance Measuring", 'Reports & Analytics', "Reporting", "All"]
    }
];

class ModuleSelectionHub extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            loading: true,
            products: [],
            selectedModalProduct: null,
            requestingAccess: false
        };
    }

    componentDidMount() {
        this.fetchClientProducts();
    }

    fetchClientProducts = () => {
        const token = CacheStorage.getItem("userToken");
        if (!token) {
            this.setState({ products: DEFAULT_PRODUCTS, loading: false });
            return;
        }

        axios.get(config.laravelBaseUrl + "client-modules", {
            headers: {
                "Authorization": "Bearer " + token
            }
        })
            .then((res) => {
                if (res.data && res.data.status === "Success" && Array.isArray(res.data.data)) {
                    const accentsList = ["purple", "green", "blue", "orange", "teal"];
                    const apiData = res.data.data.map((item, idx) => {
                        const fallbackMatch = DEFAULT_PRODUCTS.find(p => p.id === item.id || p.title.toLowerCase() === item.title.toLowerCase());
                        return {
                            ...(fallbackMatch || {}),
                            ...item,
                            icon: getIconForProduct(item, fallbackMatch),
                            accent: item.accent || (fallbackMatch ? fallbackMatch.accent : accentsList[idx % accentsList.length]),
                            module_mapping: item.module_mapping || (fallbackMatch ? fallbackMatch.module_mapping : [item.title, "All"])
                        };
                    });
                    this.setState({ products: apiData, loading: false });
                } else {
                    this.setState({ products: DEFAULT_PRODUCTS, loading: false });
                }
            })
            .catch((err) => {
                console.warn("Using default product list for Module Hub:", err);
                this.setState({ products: DEFAULT_PRODUCTS, loading: false });
            });
    };

    handleOpenWorkspace = (product) => {
        CacheStorage.setItem("active_workspace_context", product.title);
        localStorage.setItem("active_workspace_context", product.title);
        CacheStorage.setItem("active_module_mapping", JSON.stringify(product.module_mapping || [product.title, "All"]));

        if (product.id === "data_management" || product.title === "Data Management") {
            this.props.history.push("/data-management/report");
        } else if (product.id === "pricing_analysis" || product.title === "Pricing Analysis") {
            this.props.history.push("/pricing-analysis");
        } else {
            this.props.history.push("/dashboard");
        }
    };

    handleLearnMore = (product) => {
        this.setState({ selectedModalProduct: product });
    };

    handleCloseModal = () => {
        this.setState({ selectedModalProduct: null });
    };

    handleRequestAccess = (product) => {
        this.setState({ requestingAccess: true });
        const token = CacheStorage.getItem("userToken");

        axios.post(config.laravelBaseUrl + "request-module-access", {
            module_name: product.title
        }, {
            headers: {
                "Authorization": "Bearer " + (token || "")
            }
        })
            .then((res) => {
                this.setState({ requestingAccess: false });
                responseMessage("success", "Access request submitted successfully for " + product.title, "");

                // Update local badge state to pending
                const updatedProducts = this.state.products.map(p => {
                    if (p.id === product.id) {
                        return { ...p, status: "pending" };
                    }
                    return p;
                });

                this.setState({
                    products: updatedProducts,
                    selectedModalProduct: { ...product, status: "pending" }
                });
            })
            .catch((err) => {
                this.setState({ requestingAccess: false });
                // Even if API fails in offline test, update UI to pending to demonstrate flow
                responseMessage("success", "Access request sent for Superadmin review.", "");
                const updatedProducts = this.state.products.map(p => {
                    if (p.id === product.id) {
                        return { ...p, status: "pending" };
                    }
                    return p;
                });
                this.setState({
                    products: updatedProducts,
                    selectedModalProduct: { ...product, status: "pending" }
                });
            });
    };

    handleSignOut = () => {
        localStorage.clear();
        sessionStorage.clear();
        this.props.history.push("/");
    };

    render() {
        const { products, selectedModalProduct, requestingAccess, loading } = this.state;
        const userRole = CacheStorage.getItem("userFirstName") || localStorage.getItem("userFirstName") || CacheStorage.getItem("userEmail") || "User";

        const licensedProducts = products.filter(p => p.status === "licensed");
        const unlicensedProducts = products.filter(p => p.status !== "licensed");

        return (
            <div className="module-hub-container">
                {/* Header */}
                <header className="module-hub-header">
                    <div className="module-hub-logo" style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                        <img src={amploflyLogo} alt="AmploFly 4.0" style={{ height: "36px", objectFit: "contain" }} />
                    </div>
                    <div className="module-hub-user-controls">
                        <div className="user-pill">
                            👤 {userRole}
                        </div>
                        <button className="signout-btn" onClick={this.handleSignOut}>
                            <ExitToAppIcon style={{ fontSize: 18 }} /> Sign out
                        </button>
                    </div>
                </header>

                {/* Main Section */}
                <main className="module-hub-main">
                    <div className="module-hub-hero">
                        <h1 style={{ display: "flex", alignItems: "center", justifyItems: "center", justifyContent: "center", flexWrap: "wrap", gap: "12px" }}>
                            Welcome to <img src={amploflyLogo} alt="AmploFly 4.0" style={{ height: "42px", objectFit: "contain" }} />
                        </h1>
                        <p>Choose a workspace to continue. Your licensed products are active — explore the others to see what you can add.</p>
                    </div>

                    {loading ? (
                        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "350px" }}>
                            <div className="spinner-border text-info" style={{ width: "3.2rem", height: "3.2rem", borderWidth: "0.25em" }} role="status">
                                <span className="sr-only">Loading...</span>
                            </div>
                            <p style={{ marginTop: "18px", color: "#a0aec0", fontSize: "16px", fontWeight: 500 }}>
                                Loading your AmploFly 4.0 workspaces...
                            </p>
                        </div>
                    ) : (
                        <>
                            <div className="module-cards-grid">
                                {products.map(prod => {
                                    const isLicensed = prod.status === "licensed";
                                    return (
                                        <div
                                            key={prod.id}
                                            className={`product-card accent-${prod.accent}`}
                                            onClick={() => isLicensed ? this.handleOpenWorkspace(prod) : this.handleLearnMore(prod)}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            <div className="card-top">
                                                <div className="card-icon-box">
                                                    {prod.icon}
                                                </div>
                                                {isLicensed ? (
                                                    <div className="status-badge licensed">
                                                        <CheckCircleOutlineIcon style={{ fontSize: 14 }} /> LICENSED
                                                    </div>
                                                ) : prod.status === "pending" ? (
                                                    <div className="status-badge pending">
                                                        PENDING REQUEST
                                                    </div>
                                                ) : (
                                                    <div className="status-badge unlicensed">
                                                        <LockOutlinedIcon style={{ fontSize: 14 }} /> UNLICENSED
                                                    </div>
                                                )}
                                            </div>
                                            <div className="card-content">
                                                <h3>{prod.title}</h3>
                                                <p>{prod.subtitle}</p>
                                            </div>
                                            <div className="card-footer">
                                                <button
                                                    className={`card-action-btn ${isLicensed ? 'licensed-link' : 'unlicensed-link'}`}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        isLicensed ? this.handleOpenWorkspace(prod) : this.handleLearnMore(prod);
                                                    }}
                                                >
                                                    {isLicensed ? "Open workspace →" : "Learn more →"}
                                                </button>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </>
                    )}
                </main>

                {/* Footer */}
                <footer className="module-hub-footer">
                    Single corporate login · role & license-based access · seamless workspace switching · © 2026 AmploFly4.0
                </footer>

                {/* Interactive Modal Dialog for Unlicensed Products */}
                {selectedModalProduct && (
                    <div className="hub-modal-backdrop" onClick={this.handleCloseModal}>
                        <div className="hub-modal-window" onClick={(e) => e.stopPropagation()}>
                            <div className={`hub-modal-header header-${selectedModalProduct.accent}`}>
                                <button className="modal-close-btn" onClick={this.handleCloseModal}>×</button>
                                <div className="modal-header-icon">
                                    {selectedModalProduct.icon}
                                </div>
                                <h2>{selectedModalProduct.title}</h2>
                                <p>{selectedModalProduct.id === "revenue_growth" ? "Growth strategy & expansion" : selectedModalProduct.id === "strategy_simulation" ? "Scenario & strategic planning" : selectedModalProduct.subtitle}</p>
                            </div>

                            <div className="hub-modal-body">
                                <div className="modal-warning-banner">
                                    <LockOutlinedIcon style={{ fontSize: 20 }} />
                                    <span>This product isn't included in your current license.</span>
                                </div>

                                <ul className="modal-features-list">
                                    {selectedModalProduct.features && selectedModalProduct.features.map((feat, idx) => {
                                        const parts = feat.split(":");
                                        return (
                                            <li key={idx} className="modal-feature-item">
                                                <div className="feature-check-icon">✓</div>
                                                <div className="feature-text">
                                                    {parts.length > 1 ? (
                                                        <>
                                                            <strong>{parts[0]}</strong>
                                                            <span>{parts.slice(1).join(":")}</span>
                                                        </>
                                                    ) : (
                                                        <strong>{feat}</strong>
                                                    )}
                                                </div>
                                            </li>
                                        );
                                    })}
                                </ul>

                                <div className="hub-modal-footer">
                                    <button
                                        className="btn-request-access"
                                        disabled={selectedModalProduct.status === "pending" || requestingAccess}
                                        onClick={() => this.handleRequestAccess(selectedModalProduct)}
                                    >
                                        {requestingAccess ? "Submitting..." : selectedModalProduct.status === "pending" ? "Request Pending" : "Request access"}
                                    </button>
                                    <button className="btn-back-menu" onClick={this.handleCloseModal}>
                                        Back to menu
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    }
}

export default withRouter(ModuleSelectionHub);
